'use client'

import avatarIcon from '@public/images/avatar.svg'
import Image from 'next/image'
import Link from 'next/link'

import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink'
import { useTopNavV6 } from '@src/components/TopNavV6/TopNavV6Context'
import type { TopNavV6Props } from '@src/components/TopNavV6/TopNavV6Props'

import cartIcon from '../assets/mobile-cart-icon.svg'
import hamburgerIcon from '../assets/mobile-hamburger-icon.svg'

export const TopNavV6MobileBar = (props: TopNavV6Props) => {
  const {
    logo,
    cartHref = '#cart',
    onCartClick,
    onAccountClick,
    mobileHomeButtonText = 'Home',
  } = props
  const { setIsMobileMenuOpen, setAccountExpanded } = useTopNavV6()

  const handleAccountClick = () => {
    if (onAccountClick) {
      onAccountClick()
    } else {
      setAccountExpanded(true)
    }
  }

  return (
    <>
      {/* Top Bar - Logo Only */}
      <div
        className="relative flex h-[47px] w-full items-center justify-center border-b border-bodi-gray-dark bg-bodi-black-text"
        data-name="Mobile - Top Bar"
      >
        {logo?.src && (
          <LocalizedLink href={logo.href ?? '/'} className="flex shrink-0 items-center">
            <Image
              className="h-[25px] w-[76px]"
              src={logo.src}
              width={300}
              height={150}
              alt={logo.altText ?? 'Logo'}
            />
          </LocalizedLink>
        )}
      </div>

      {/* Bottom Bar - Navigation Controls */}
      <div
        className="relative flex h-12 w-full items-center justify-between border-b border-bodi-gray-dark bg-bodi-black-text px-4"
        data-name="Mobile - Bottom Bar"
      >
        {/* Left Side - Hamburger Menu and Home Button */}
        <div className="flex items-center gap-1">
          {/* Hamburger Menu Icon */}
          <button
            data-testid="topNavMobileMenu"
            className="flex size-6 appearance-none items-center justify-center"
            onClick={() => setIsMobileMenuOpen(true)}
          >
            <Image src={hamburgerIcon} width={24} height={24} alt="Menu" />
          </button>

          {/* Home Button */}
          <div className="flex h-full items-center border-l border-bodi-gray-medium py-0 pr-1 pl-2">
            <button
              className="text-body-md leading-[18px] font-semibold text-bodi-gray-butters uppercase"
              data-name="Home Button"
              onClick={() => setIsMobileMenuOpen(true)}
            >
              {mobileHomeButtonText}
            </button>
          </div>
        </div>

        {/* Right Side - Cart and Account */}
        <div className="flex items-center gap-3">
          {/* Cart Icon with Badge */}
          <div className="flex h-11 w-7 items-center justify-center py-4">
            <Link
              className="relative"
              data-testid="cartBtn"
              href={cartHref}
              onClick={onCartClick}
              scroll={false}
            >
              <Image
                src={cartIcon}
                width={24}
                height={24}
                alt="Shopping cart"
                className="h-6 w-6 object-cover"
              />
              {/* TODO: Add cart quantity badge once we have Shopify cart sync working */}
              {/* {cartQuantity > 0 ? (
                <div
                  className={cn(
                    'absolute top-0 right-[-10px] -mt-2 flex min-h-[23px] min-w-[23px] items-center justify-center rounded-full border-2 border-bodi-black-text bg-bodi-green-bright-500 text-body-xs font-semibold text-bodi-black',
                    { 'px-1.5': cartQuantity > 99 }
                  )}
                >
                  {cartQuantity > 99 ? '99' : cartQuantity}
                </div>
              ) : null} */}
            </Link>
          </div>

          {/* Account/Avatar Icon */}
          <div className="flex h-11 items-center justify-end py-4">
            <button
              onClick={handleAccountClick}
              data-testid="accountBtn"
              className="flex h-[22px] w-6 items-center justify-center"
            >
              <Image src={avatarIcon} width={22} height={22} alt="Account icon" />
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
